import { useState, useMemo, useCallback } from 'react'
import dayjs from 'dayjs'
import cx from 'classnames'

import { Modal } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'

import { BrandPlan, ContentStrategy, ContentStrategyType } from 'types'
import { ContentStrategyTypeLabel } from '../../../constants'
import { isActiveStrategy } from '../strategyUtils'
import { ContentStrategyTypeEmoji } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ImgMap } from 'components/ContentStrategyTypeImg/ContentStrategyTypeImg'
import { description as strategyDescription } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/utils'

import Styles from './StrategySelection.module.scss'

interface StrategySelectionProps {
  brandPlan: BrandPlan
  onNext: (strategyType: ContentStrategyType, strategyConfig: ContentStrategy, campaignCount: number) => void
  onCancel: () => void
}

const CAMPAIGN_COUNT_OPTIONS = [1, 2, 3, 4, 5] as const

export const StrategySelection = ({ brandPlan, onNext, onCancel }: StrategySelectionProps) => {
  const [campaignCount, setCampaignCount] = useState(1)
  const [customCount, setCustomCount] = useState<string>('')
  const [isCustom, setIsCustom] = useState(false)
  const [selectedStrategy, setSelectedStrategy] = useState<ContentStrategyType | null>(null)

  const contentStrategiesEntries = useMemo(() => {
    if (!brandPlan.content_strategies) return []
    return Object.entries(brandPlan.content_strategies).filter(
      (entry): entry is [ContentStrategyType, ContentStrategy] => entry[1] != null,
    )
  }, [brandPlan.content_strategies])

  const activeStrategies = useMemo(
    () => contentStrategiesEntries.filter(([, strategy]) => isActiveStrategy(strategy)),
    [contentStrategiesEntries],
  )

  const upcomingStrategies = useMemo(
    () =>
      contentStrategiesEntries
        .filter(([, strategy]) => !isActiveStrategy(strategy))
        .sort(([, a], [, b]) => dayjs(a.start_date).diff(dayjs(b.start_date))),
    [contentStrategiesEntries],
  )

  const effectiveSelected = selectedStrategy ?? activeStrategies[0]?.[0] ?? upcomingStrategies[0]?.[0] ?? null
  const effectiveCount = isCustom ? parseInt(customCount, 10) || 1 : campaignCount

  const handleNext = useCallback(() => {
    if (!effectiveSelected) return
    const config = brandPlan.content_strategies?.[effectiveSelected]
    if (!config) return
    onNext(effectiveSelected, config, effectiveCount)
  }, [effectiveSelected, effectiveCount, brandPlan.content_strategies, onNext])

  const handleCountClick = (count: number) => {
    setIsCustom(false)
    setCampaignCount(count)
  }

  const renderStrategyCard = (type: ContentStrategyType, strategy: ContentStrategy) => {
    const isSelected = effectiveSelected === type
    const startDate = dayjs(strategy.start_date || brandPlan.start_date).format('ddd, MMM D YYYY')
    const img = ImgMap[type]

    return (
      <div
        key={type}
        className={cx(Styles.strategyCard, { [Styles.selected]: isSelected })}
        onClick={() => setSelectedStrategy(type)}
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
      >
        <div className={cx(Styles.cardInner, Styles[type])}>
          <div className={Styles.cardContent}>
            <Heading level={3} color="var(--light-100)">
              <ContentStrategyTypeEmoji contentStrategyType={type} /> {ContentStrategyTypeLabel[type]}
            </Heading>
            <Text variant="secondary" color="var(--light-90)">
              {strategyDescription[type]}
            </Text>
          </div>
          {img && <img src={img} alt={ContentStrategyTypeLabel[type]} className={Styles.cardImg} />}
          <div className={Styles.cardFooter}>
            <Text variant="secondary" color="var(--light-60)">
              Started on {startDate}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal.Content className={Styles.content}>
        <div className={Styles.section}>
          <p className={Styles.sectionTitle}>How many campaigns do you want to create?</p>
          <div className={Styles.countSelector}>
            {CAMPAIGN_COUNT_OPTIONS.map((count) => (
              <button
                key={count}
                className={cx(Styles.countButton, { [Styles.active]: !isCustom && campaignCount === count })}
                onClick={() => handleCountClick(count)}
              >
                {count}
              </button>
            ))}
            {isCustom ? (
              <input
                type="text"
                inputMode="numeric"
                value={customCount}
                onChange={(e) => setCustomCount(e.target.value.replace(/[^0-9]/g, ''))}
                className={Styles.customInputInline}
                autoFocus
              />
            ) : (
              <button
                className={Styles.countButton}
                onClick={() => {
                  setIsCustom(true)
                  setCustomCount('10')
                }}
              >
                Custom
              </button>
            )}
          </div>
        </div>

        <div className={Styles.section}>
          <p className={Styles.sectionTitle}>Choose a strategy for your campaigns?</p>

          <div className={Styles.strategyGroups}>
            {activeStrategies.length > 0 && (
              <div className={Styles.strategyGroup}>
                <Text variant="metadata" color="var(--dark-40)">
                  Active Strategies
                </Text>
                <div className={Styles.strategyList}>
                  {activeStrategies.map(([type, strategy]) => renderStrategyCard(type, strategy))}
                </div>
              </div>
            )}

            {upcomingStrategies.length > 0 && (
              <div className={Styles.strategyGroup}>
                <Text variant="metadata" color="var(--dark-40)">
                  Upcoming Strategies
                </Text>
                <div className={Styles.strategyList}>
                  {upcomingStrategies.map(([type, strategy]) => renderStrategyCard(type, strategy))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={onCancel} variant="tertiary" size="lg">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleNext}
            isDisabled={!effectiveSelected || effectiveCount < 1}
          >
            Next, Set Default Content Types
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
